import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import useAuthRedirect from '../../hooks/useAuthRedirect';
import { getErrorMessage } from './authError';

// Loosely RFC-valid email check used to enable the submit button.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shared "request a one-time link" screen used by both /forgot-password and
 * /activate. Always shows the same generic confirmation on any 200 so account
 * existence never leaks. Only copy and the dispatched thunk differ.
 *
 * Props:
 *  - thunk        redux thunk dispatched with ({ email })
 *  - side         { eyebrow, headline, paragraph } for the left pitch panel
 *  - bracket      eyebrow chip text, e.g. "[ Security · Password reset ]"
 *  - headline     right-panel H1
 *  - lede         right-panel intro paragraph (mentions link expiry)
 *  - emailHint    hint copy under the email field
 *  - ctaLabel     primary button label
 *  - confirmTitle generic-confirmation H1
 *  - confirmBody  generic-confirmation paragraph (anti-enumeration)
 *  - confirmNote  expiry / check-spam note in the confirmation state
 */
export default function RequestLinkForm({
  thunk,
  side,
  bracket,
  headline,
  lede,
  emailHint,
  ctaLabel,
  confirmTitle,
  confirmBody,
  confirmNote,
}) {
  const dispatch = useDispatch();
  // Logged-in users hitting this public route get bounced to /sites.
  useAuthRedirect('/sites', '/login');

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const emailValid = EMAIL_RE.test(email.trim());

  const sendLink = async () => {
    setError('');
    setSubmitting(true);
    try {
      await dispatch(thunk({ email: email.trim() })).unwrap();
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not send the link. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!emailValid) return;
    sendLink();
  };

  return (
    <div className="auth-page">
      <section className="auth-side">
        <div className="auth-brand">
          <img src="/nc-logo-white.svg" alt="North Commerce" />
        </div>
        <div className="auth-pitch">
          <span className="auth-eyebrow">{side.eyebrow}</span>
          <h1>{side.headline}</h1>
          <p>{side.paragraph}</p>
        </div>
      </section>

      <main className="auth-form-panel">
        <div className="auth-meta">
          Remembered it? <Link to="/login">Back to sign in</Link>
        </div>

        <div className="auth-form-wrap">
          <span className="auth-bracket">{bracket}</span>

          {submitted ? (
            <>
              <h2>{confirmTitle}</h2>
              <p>{confirmBody}</p>
              <div className="auth-form-body">
                <p className="hint">{confirmNote}</p>
                <button
                  className="btn btn-primary full-width"
                  type="button"
                  onClick={sendLink}
                  disabled={submitting}
                >
                  {submitting ? 'Resending…' : 'Resend link'}
                </button>
                <div className="auth-legal">
                  Remembered it? <Link to="/login">Back to sign in</Link>.
                </div>
              </div>
            </>
          ) : (
            <>
              <h2>{headline}</h2>
              <p>{lede}</p>

              <form className="auth-form-body" onSubmit={onSubmit}>
                {error ? <div className="error-banner">{error}</div> : null}

                <div className="form-field">
                  <label htmlFor="email">Email</label>
                  <div className="input-icon-wrap">
                    <Mail size={16} />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </div>
                  <span className="hint">{emailHint}</span>
                </div>

                <button
                  className="btn btn-primary full-width"
                  type="submit"
                  disabled={!emailValid || submitting}
                >
                  {submitting ? 'Sending…' : ctaLabel}
                </button>

                <div className="auth-legal">
                  Remembered it? <Link to="/login">Back to sign in</Link>.
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
